// Pure, dependency-free (no Angular DI) role/affiliate-claim classification helpers.
//
// Extracted out of AuthService (AppSec High Finding #9 assurance work, 2026-09-29) so
// AuthGuardService can also use the EXACT same claim-classification rules to synchronously
// resolve an authoritative role tier at route-activation time, without creating a circular
// DI dependency (AuthService already injects AuthGuardService - AuthGuardService injecting
// AuthService back would be a circular constructor dependency Angular cannot resolve).
// AuthService's public methods of the same name are now thin wrappers around these
// functions - every existing caller of AuthService's methods is unaffected.
//
// Do NOT reintroduce any of this logic directly inside AuthGuardService or any component -
// add new affiliate/role-claim rules ONLY here.

export type RoleTier = 'Admin' | 'ApproverAdmin' | 'User';

// Single source of truth for company-name/role-code -> canonical affiliate code mapping.
// Add any future affiliate/company-name synonym here - nowhere else.
const AFFILIATE_CODE_MAP: Record<string, string> = {
  'TMNA': 'TMN',
  'Toyota Motor Europe NV/SA': 'TME',
  'Toyota Motor Manufacturing Fr': 'TMMF',
};

/**
 * Normalizes a raw affiliate code (e.g. "TMNA") OR a full Company Name claim (e.g.
 * "Toyota Motor Europe NV/SA") into the canonical short affiliate code. Values with no
 * known synonym pass through unchanged.
 */
export function mapAffiliate(value: string): string {
  if (!value) {
    return value;
  }
  return AFFILIATE_CODE_MAP[value] ?? value;
}

/**
 * Extracts the affiliate-code portion of a single "AFFILIATE-Role" claim string, e.g.
 * "TMN-Admin" -> "TMN".
 */
export function extractAffiliateCode(roleClaim: string): string {
  return roleClaim.replace(/-\S+/g, '');
}

/**
 * Resolves the single canonical affiliate code for a user from their raw role claims.
 * Prefers a role claim containing "User" (the account's base/home affiliate membership)
 * over roles[0], since Azure AD does not guarantee role-claim ordering.
 */
export function resolveAffiliateCode(roles: string[] | null | undefined): string {
  if (!roles || roles.length === 0) {
    return '';
  }
  const userRole = roles.find((r) => r.includes('User'));
  const chosen = userRole ?? roles[0];
  return mapAffiliate(extractAffiliateCode(chosen));
}

/**
 * Classifies the app-level role tier purely from the role claims + the affiliate the user
 * is currently scoped to. Admin/Oversight are fully determined by claims alone. A return of
 * 'User' means the claims show ordinary membership only - the final Approver/User/
 * Unauthorized split still requires a server-side is_approver lookup. Returns null when no
 * claim matches this affiliate at all.
 */
export function classifyRoleTier(roles: string[] | null | undefined, affiliate: string): RoleTier | null {
  if (!roles || roles.length === 0 || !affiliate) {
    return null;
  }
  const rolesUpper = roles.map((r) => r.toUpperCase());
  const code = affiliate.toUpperCase();
  if (rolesUpper.includes(`${code}-ADMIN`)) {
    return 'Admin';
  }
  if (rolesUpper.includes(`${code}-OVERSIGHT`)) {
    return 'ApproverAdmin';
  }
  if (rolesUpper.includes(`${code}-USER`)) {
    return 'User';
  }
  return null;
}
